/* iter55o — Extracted from EditorPanel.jsx (was 670 LOC).
 *
 * The dashed-border "Write from scratch" canvas that appears when a
 * chapter has no paragraphs yet. Two responsibilities:
 *   • Give the writer an immediately-usable contentEditable field
 *     with hint text + Cmd/Ctrl+Enter to commit, Esc to clear.
 *   • Offer a one-click "…or Draft with AI" affordance when the
 *     user has the `ai_write_chapters` capability (Pro+).
 *
 * Zero behavioural change vs the inline version — same handlers,
 * same testids, same styling. Pure sub-component refactor.
 */
import React from 'react';
import { PenLine, Sparkles } from 'lucide-react';
import CoachHint from '@/components/coach/CoachHint';

export const BlankChapterCanvas = React.forwardRef(function BlankChapterCanvas({
  chapterId,
  hover, setHover,
  canGenerate,
  canAiWriteChapters,
  onGenerate,
  onSave,
}, ref) {
  const canvasHint = canAiWriteChapters
    ? {
        label: 'Two ways to start',
        body:
          'Type your opening line here (auto-saves on blur, Cmd/Ctrl+Enter to commit) — ' +
          'or hover the frame and click "Draft with AI" to let Claude take the first pass.',
      }
    : {
        label: 'Start your chapter',
        body:
          'Type your opening sentence — even a placeholder helps you get moving. ' +
          'Every save is versioned, so nothing you write can be lost.',
      };

  return (
    <CoachHint {...canvasHint} side="right" align="start" testId="coach-blank-canvas">
      <div
      className="group relative rounded-lg border border-dashed border-panel-border/70 bg-card/30 p-1 transition-colors hover:border-primary/50 focus-within:border-primary"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid="manuscript-blank-frame"
    >
      <div className="flex items-center justify-between px-3 pt-1.5 text-[13px] uppercase tracking-wider text-muted-foreground/80">
        <span className="inline-flex items-center gap-1.5">
          <PenLine className="h-3 w-3" /> Write from scratch
        </span>
        {hover && canGenerate && (
          <button
            type="button"
            onClick={() => onGenerate(chapterId)}
            title={canAiWriteChapters
              ? 'Let Claude draft the first version'
              : 'Available on Pro Author — click for details'}
            data-testid="manuscript-blank-generate-btn"
            className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[13px] font-medium transition-colors ${
              canAiWriteChapters
                ? 'bg-primary/10 text-primary hover:bg-primary/20'
                : 'bg-muted/40 text-muted-foreground hover:bg-muted/60'
            }`}
          >
            <Sparkles className="h-3 w-3" />
            {canAiWriteChapters ? '…or Draft with AI' : '…or Draft with AI (Pro)'}
          </button>
        )}
      </div>
      <p
        key={`blank-${chapterId}`}
        ref={ref}
        className="min-h-[10rem] px-3 py-3 outline-none cursor-text empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground/50 empty:before:italic"
        contentEditable="true"
        suppressContentEditableWarning
        data-gramm="true"
        data-gramm_editor="true"
        data-enable-grammarly="true"
        data-placeholder="Start writing this chapter here… (auto-saves on blur · Cmd/Ctrl+Enter to commit · Esc to clear)"
        onFocus={() => setHover(true)}
        onBlur={(e) => {
          setHover(false);
          onSave(0, e.currentTarget.innerText);
        }}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            e.currentTarget.innerText = '';
            e.currentTarget.blur();
          }
        }}
        data-testid="manuscript-blank-canvas"
      />
    </div>
    </CoachHint>
  );
});
